import {useCallback, useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {FOR_YOU_MAX_PRODUCTS, FOR_YOU_PAGE_SIZE} from '../constants'
import {useFypRecosPLP} from './use-fyp-recos-plp'

const initialState = (categoryId, page) => ({
    categoryId,
    page,
    products: [],
    loadedPages: [],
    minPageLoaded: page,
    maxPageLoaded: page,
    total: 0
})

/**
 * Reads the page to start on, so a deep link / reload lands on the same page as the PLP
 * (`?page=2&offset=36`). Falls back to the first page and clamps to the product ceiling.
 */
const readInitialPage = (search, pageSize, maxProducts) => {
    const params = new URLSearchParams(search || '')
    const pageParam = parseInt(params.get('page'), 10)
    const offsetParam = parseInt(params.get('offset'), 10)

    let page = 1
    if (Number.isFinite(pageParam) && pageParam > 0) {
        page = pageParam
    } else if (Number.isFinite(offsetParam) && offsetParam > 0) {
        page = Math.floor(offsetParam / pageSize) + 1
    }

    const lastPage = Math.max(1, Math.ceil(maxProducts / pageSize))
    return Math.min(page, lastPage)
}

/**
 * Paginated For You products with PLP-style pagination:
 *   - "Show More" appends the next page and advances the URL (`?page=N&offset=X`).
 *   - "Show Previous" prepends the page before the first loaded page (URL unchanged).
 *   - A deep link / reload starts on the URL's page.
 *
 * Each page is fetched and enriched on its own, so tiles are never enriched in bulk.
 */
export const useFypLandingProducts = ({
    categoryId,
    enabled = true,
    pageSize = FOR_YOU_PAGE_SIZE,
    maxProducts = FOR_YOU_MAX_PRODUCTS
}) => {
    const location = useLocation()
    const history = useHistory()

    const [state, setState] = useState(() =>
        initialState(categoryId, readInitialPage(location.search, pageSize, maxProducts))
    )
    const [isLoadingNewPage, setIsLoadingNewPage] = useState(false)

    // Reset during render so a page fetched for the previous category is never applied.
    if (state.categoryId !== categoryId) {
        setState(initialState(categoryId, readInitialPage(location.search, pageSize, maxProducts)))
    }
    const current =
        state.categoryId === categoryId
            ? state
            : initialState(categoryId, readInitialPage(location.search, pageSize, maxProducts))

    const offset = (current.page - 1) * pageSize
    const limit = Math.min(pageSize, Math.max(maxProducts - offset, 0))
    const isEnabled = enabled && Boolean(categoryId) && limit > 0

    const {page: pageResult, isFailed} = useFypRecosPLP({
        categoryId,
        offset,
        limit,
        enabled: isEnabled,
        useRecommendations: true,
        recommendationCount: maxProducts
    })

    useEffect(() => {
        if (!pageResult || pageResult.offset !== offset) return

        setState((previous) => {
            if (previous.categoryId !== categoryId) return previous
            if (previous.loadedPages.includes(current.page)) return previous

            const seen = new Set(previous.products.map((item) => item?.id))
            const added = (pageResult.hits || []).filter((hit) => hit?.id && !seen.has(hit.id))
            const isPrepend = current.page < previous.minPageLoaded

            return {
                ...previous,
                products:
                    added.length === 0
                        ? previous.products
                        : isPrepend
                        ? [...added, ...previous.products]
                        : [...previous.products, ...added],
                loadedPages: [...previous.loadedPages, current.page],
                minPageLoaded: Math.min(previous.minPageLoaded, current.page),
                maxPageLoaded: Math.max(previous.maxPageLoaded, current.page),
                total: Math.min(pageResult.total ?? 0, maxProducts)
            }
        })
        setIsLoadingNewPage(false)
    }, [pageResult, offset, current.page, categoryId, maxProducts])

    // A failed page must not leave the buttons stuck in a loading state.
    useEffect(() => {
        if (isFailed) setIsLoadingNewPage(false)
    }, [isFailed])

    const goToPage = useCallback(
        (newPage) => {
            if (newPage < 1) return
            if ((newPage - 1) * pageSize >= maxProducts) return

            setState((previous) => ({...previous, page: newPage}))
            setIsLoadingNewPage(true)

            if (newPage > current.maxPageLoaded) {
                const params = new URLSearchParams(location.search)
                const newOffset = (newPage - 1) * pageSize
                params.set('page', newPage)
                if (newPage > 1) params.set('offset', newOffset)
                else params.delete('offset')
                history.replace(`${location.pathname}?${params.toString()}`)
            }
        },
        [pageSize, maxProducts, current.maxPageLoaded, location.pathname, location.search, history]
    )

    const products = current.products
    const total = current.total
    const isLoading = isEnabled && products.length === 0 && !isFailed
    const isLoadingMore = isLoadingNewPage && current.page > current.maxPageLoaded
    const isLoadingPrevious = isLoadingNewPage && current.page < current.minPageLoaded
    const hasMore = !isFailed && products.length > 0 && current.maxPageLoaded * pageSize < total
    const showLoadPrevious = current.minPageLoaded > 1

    const showMore = useCallback(
        () => goToPage(current.maxPageLoaded + 1),
        [goToPage, current.maxPageLoaded]
    )
    const loadPrevious = useCallback(
        () => goToPage(current.minPageLoaded - 1),
        [goToPage, current.minPageLoaded]
    )

    return {
        products,
        total,
        isLoading,
        isLoadingMore,
        isLoadingPrevious,
        isFailed,
        hasMore,
        showLoadPrevious,
        showMore,
        loadPrevious
    }
}
